import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/app/context/toast-provider'
import { createCandidateNoteRequest, deleteCandidateNoteRequest, getNotesByEmailRequest, updateCandidateNoteRequest } from '@/app/utils/handlers/client/notes'
import { candidateNotesQueryKeys } from '@/constants/cache-keys.contant'
import { parseAxiosError } from '@/utils/parse-axios-error.util'
import type { AxiosResponse } from 'axios'

export type CandidateNote = {
    id: number
    note: string
    candidateId?: number | null
    jobApplicationId?: number | null
    createdAt: string
    createdBy?: {
        name?: string
        email?: string
    }
}

export type CandidateNotesData = {
    candidateNotes: CandidateNote[]
    jobApplicationNotes: CandidateNote[]
    jobApplicationsMap: Record<number, { jobPostingId: number; jobTitle: string }>
}

type NotesApiResponse<T> = {
    data: T
    message?: string
}

async function unwrapNotesRequest<T>(request: Promise<AxiosResponse<NotesApiResponse<T>>>): Promise<{ data: T; message?: string }> {
    try {
        const { data } = await request
        return { data: data.data, message: data.message }
    } catch (error) {
        throw new Error(parseAxiosError(error))
    }
}

export const useCandidateNotesQuery = (email?: string, companyId?: number) => {
    return useQuery({
        queryKey: candidateNotesQueryKeys.byEmail(email ?? '', companyId ?? 0),
        queryFn: async (): Promise<CandidateNotesData> => {
            const { data } = await unwrapNotesRequest<CandidateNotesData>(getNotesByEmailRequest(email!, companyId!))
            return {
                candidateNotes: data?.candidateNotes ?? [],
                jobApplicationNotes: data?.jobApplicationNotes ?? [],
                jobApplicationsMap: data?.jobApplicationsMap ?? {},
            }
        },
        enabled: Boolean(email && companyId),
    })
}

export const useCandidateNotesMutations = (email?: string, companyId?: number) => {
    const queryClient = useQueryClient()
    const { showSuccessToast, showErrorToast } = useToast()

    const invalidateNotes = () =>
        queryClient.invalidateQueries({
            queryKey: email && companyId ? candidateNotesQueryKeys.byEmail(email, companyId) : candidateNotesQueryKeys.all,
        })

    const createMutation = useMutation({
        mutationFn: (payload: { note: string; candidateId: number | null; jobApplicationId: number | null }) =>
            unwrapNotesRequest<CandidateNote>(createCandidateNoteRequest(payload)),
        onSuccess: async ({ message }) => {
            showSuccessToast(message ?? 'Note created successfully')
            await invalidateNotes()
        },
        onError: (error: Error) => {
            showErrorToast(error.message || 'Failed to save note')
        },
    })

    const updateMutation = useMutation({
        mutationFn: ({ id, note }: { id: number; note: string }) => unwrapNotesRequest<CandidateNote>(updateCandidateNoteRequest(id, { note })),
        onSuccess: async ({ message }) => {
            showSuccessToast(message ?? 'Note updated successfully')
            await invalidateNotes()
        },
        onError: (error: Error) => {
            showErrorToast(error.message || 'Failed to update note')
        },
    })

    const deleteMutation = useMutation({
        mutationFn: (id: number) => unwrapNotesRequest<CandidateNote>(deleteCandidateNoteRequest(id, {})),
        onSuccess: async ({ message }) => {
            showSuccessToast(message ?? 'Note deleted successfully')
            await invalidateNotes()
        },
        onError: (error: Error) => {
            showErrorToast(error.message || 'Failed to delete note')
        },
    })

    return {
        createMutation,
        updateMutation,
        deleteMutation,
        invalidateNotes,
    }
}
